import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * Shared page header for Operate dashboards (Health, Portfolio, Performance, Journals).
 *
 * @param {string}       title       - Page title
 * @param {string}       description - Subtitle / description line
 * @param {ReactNode}    titleAdornment - Optional control rendered immediately after the title
 * @param {ReactNode}    actions     - Optional controls rendered on the right
 * @param {boolean}      hideTitle   - Hide title (e.g. mobile shell already shows one)
 */
export default function DashboardPageHeader({ title, description, actions, titleAdornment = null, hideTitle = false }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);

  if (hideTitle && !description && !actions) return null;
  if (isMobile && hideTitle && !actions) return null;

  const showTitleBlock = !hideTitle || (description && !isMobile);
  const actionsOnlyMobile = isMobile && hideTitle && !!actions;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '24px',
      }}
    >
      {showTitleBlock && (
        <div style={{ minWidth: 0, flex: 1 }}>
          {!hideTitle && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <h2
                style={{
                  color: colors.text,
                  fontSize: isMobile ? '1.35rem' : '2rem',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: 1.15,
                }}
              >
                {title}
              </h2>
              {titleAdornment}
            </div>
          )}
          {description && !isMobile && (
            <p style={{ color: colors.textLight, margin: hideTitle ? 0 : '6px 0 0', fontSize: '0.875rem' }}>
              {description}
            </p>
          )}
        </div>
      )}
      {actions && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            flexShrink: 0,
            ...(actionsOnlyMobile
              ? { width: '100%', flex: '1 1 100%' }
              : {}),
          }}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
